
import React from 'react';
import { Language, Translation } from '../types';
import { LanguageSwitcher } from './LanguageSwitcher';

interface Props {
  lang: Language;
  t: Translation;
  onLangChange: (l: Language) => void;
  isSticky?: boolean;
  onMenuToggle: (isOpen: boolean) => void;
  isMenuOpen: boolean;
  onNavigate: (page: string) => void;
  currentPage: string;
}

export const Header: React.FC<Props> = ({ 
  lang, 
  t, 
  onLangChange, 
  isSticky = false, 
  onMenuToggle,
  isMenuOpen,
  onNavigate,
  currentPage
}) => {
  const NavItem = ({ id, label }: { id: string, label: string }) => (
    <button 
      onClick={() => onNavigate(id)}
      className={`text-[10px] font-bold uppercase tracking-[0.2em] transition-all py-2 border-b-2 ${
        currentPage === id ? 'text-stone-900 border-stone-900' : 'text-stone-400 border-transparent hover:text-stone-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <header className={`sticky top-0 left-0 right-0 z-[80] transition-all duration-300 ${
      isSticky 
        ? 'bg-white/95 backdrop-blur-md shadow-lg py-2 lg:py-3' 
        : 'bg-white py-3 lg:py-6 border-b border-stone-100'
    }`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center space-x-3 cursor-pointer group" onClick={() => onNavigate('home')}>
          <div className="w-8 h-8 lg:w-9 lg:h-9 bg-stone-900 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105">
            <span className="text-white font-black text-lg lg:text-xl">S</span>
          </div>
          <span className="font-black tracking-tighter text-lg lg:text-xl text-stone-900">STEALTH</span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-10">
          <NavItem id="home" label={t.header.howItWorks} />
          <NavItem id="investment" label={t.header.investment} />
          <NavItem id="gallery" label={t.header.gallery} />
          <NavItem id="blog" label={t.header.blog} />
          <NavItem id="faq" label="FAQ" />
          <NavItem id="contacts" label={t.header.contacts} />
        </nav>

        <div className="flex items-center space-x-6">
          <a 
            href="https://wa.me/420608099059" 
            target="_blank" 
            className="hidden sm:flex items-center space-x-2 bg-emerald-500 text-white px-5 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all transform hover:scale-105 active:scale-95 shadow-md"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.767 5.767 0 1.267.405 2.436 1.096 3.389l-.715 2.614 2.678-.703c.82.505 1.776.797 2.802.797 3.181 0 5.767-2.586 5.767-5.767 0-3.181-2.586-5.767-5.861-5.767zm3.308 8.213c-.154.438-.764.795-1.127.842-.325.043-.75.076-1.213-.076-2.028-.667-3.342-2.731-3.441-2.864-.1-.133-.807-1.072-.807-2.046 0-.974.505-1.45.684-1.649.179-.199.392-.249.524-.249s.262.003.376.009c.125.007.292-.046.458.353.166.399.565 1.378.614 1.477.05.099.083.216.017.349s-.099.216-.199.332c-.099.116-.211.262-.303.353-.105.105-.216.216-.093.428.123.211.545.899 1.166 1.45.8.71 1.472.93 1.683 1.035.211.105.335.088.459-.055.123-.143.53-.615.671-.825.143-.21.285-.177.481-.105.196.072 1.242.585 1.455.694.213.109.355.163.407.253.052.09.052.522-.102.96z"/></svg>
            <span>WhatsApp</span>
          </a>
          
          <div className="hidden lg:block">
            <LanguageSwitcher current={lang} onSelect={onLangChange} />
          </div>

          <button 
            className="lg:hidden p-2 text-stone-900 rounded-xl hover:bg-stone-50 transition-colors"
            onClick={() => onMenuToggle(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};
